/**
 * @author Ka Wing Chin <(kawing.chin@takeaway.com>
 * @class Takeaway.Basket
 */

/* global localStorage */

import ActiveDiscount from './active-discount'
import AutoAddProduct from './auto-add-product'
import Category from './category'
import Discount from './discount'
import ENUM from '../lib/enums'
import PaymentMethod from './payment-method'
import Product from './product'
import Restaurant from './restaurant'
import Utils from '../lib/utils'
import Voucher from './voucher'

export default class Basket {
  /**
   * @enum
   * @property {Number} TIME.TTL Time To Live
   */
  Time = Object.freeze({
    TTL: 30 * 60 * 1000 // 30 minutes
  })

  /**
   * Constructor
   * @param {Takeaway.Restaurant} restaurantModel.
   * @param {ENUM.DeliveryMethod.Type} deliveryType
   */
  constructor (restaurantModel, deliveryType, serverDateTime) {
    this.serverDateTime = serverDateTime
    this.basketProducts = []
    this.productMismatches = []
    this.productMissings = []

    // console.log("Init Basket " + restaurantModel.id);
    this.restaurant = restaurantModel
    this.id = this.restaurant.id
    this.deliveryType = deliveryType
    this.setDeliveryAreaById(this.restaurant.deliveryAreaId)
    this.loadBasket(this.id)
    this.switchDeliveryType(this.deliveryType, true)
  }

  static PRODUCT = Object.freeze({
    SUCCESS: 0,
    RESTAURANT_CLOSED: 1,
    CATEGORY_UNAVAILABLE: 2
  })

  /**
   * @property {ENUM.DeliveryMethod.Type} deliveryType the basket is using. Use ENUM.DeliveryMethod.Type for comparison.
   */
  deliveryType
  isCheckoutAllowed = false
  voucher = null
  voucherValidation = {
    success: false,
    minimumAmount: false,
    productRequired: false
  }
  autoAddProducts = []
  /**
   * Products that are currently in the basket.
   */
  basketProducts
  /**
   * Products that are mismatched. It will be cleared at every validation.
   * @property Array<Product>
   */
  productMismatches
  /**
   * Products that are missing. It will be cleared at every validation.
   * @property Array<Product>
   */
  productMissings
  /**
   * Discount that are available in the restaurant.
   * @property Array<Discount>
   */
  discounts = []
  /**
   * Current discounts that are applied to the basket.
   * @property Array<ActiveDiscount>
   */
  activeDiscounts = []
  /**
   * Current delivery area.
   */
  deliveryArea
  /**
   * Current delivery cost that is applied to the basket.
   */
  deliveryCosts = 0
  /**
   * The total base price, delivery costs and transaction costs excluded.
   */
  baseTotalPrice = 0
  /**
   * @property {float} minimumOrderValueTotalPrice The total base price excluding product minimum order value. This is included discount.
   */
  minimumOrderValueTotalPrice = 0
  /**
   * @property {float} excludeMinimumOrderValueTotalPrice The total base price of XMF products.
   */
  excludeMinimumOrderValueTotalPrice = 0
  /**
   * @property {float} autoAddProductsTotalPrice The total price of the auto add products.
   */
  autoAddProductsTotalPrice = 0
  /**
   * The total price, every costs included.
   */
  totalPrice = 0
  totalProducts = 0
  /**
   * @property {PaymentMethod}
   * The selected payment method.
   */
  paymentMethod = null
  transactionCosts
  /**
   * @property {boolean} isValidateBasketByServer Whether the basket is currently in validation or not.
   */
  isValidateBasketByServer = false

  /**
   * here we will hold all the age restriction items.
   * [{uniqueId: '2222', minAge: 18}]
   * @type {Array}
   */
  ageRestrictionsCart = []

  /**
   * Set the current delivery area
   * @param {string} id of the delivery area.
   */
  setDeliveryAreaById (id) {
    this.deliveryArea = this.restaurant.getDeliveryAreaById(id)
  }

  /**
   * Switch delivery type. It will check if the restaurant supports it.
   * @param {int} deliveryType Takeaway.ENUM.DeliveryMethod.Type
   * @param {boolean} force Whether to skip the validation or not.
   * @return {object} literal which contains the error data.
   */
  switchDeliveryType (deliveryType, force) {
    var errors
    if (!force) {
      errors = this.validateRestaurant()
      if (!errors.restaurantSupportedDeliveryType) {
        return errors
      }

      errors = this.validateBasket(deliveryType)
      if (errors.unsupportedDeliveryType.length > 0 || errors.priceDifference.length > 0) {
        return errors
      }
    }

    this.deliveryType = deliveryType
    for (var i = 0; i < this.basketProducts.length; i++) {
      var basketProduct = this.basketProducts[i]
      basketProduct.isDisabled = false
      if (basketProduct.deliveryType !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        if (basketProduct.size !== null) {
          if (basketProduct.size.deliveryType !== this.deliveryType) {
            console.log('switchDeliveryType() basketProduct', basketProduct)
            this.removeProduct(basketProduct.uniqueId)
          }
        } else if (basketProduct.deliveryType !== this.deliveryType) {
          this.removeProduct(basketProduct.uniqueId)
        }
      }
    }
    this.calculateTotalPrice()
    this.restaurant.getOpeningStatus(this.serverDateTime, this.deliveryType)
    return errors
  }

  /**
   * Calculate the total price.
   */
  calculateTotalPrice () {
    this.totalPrice = 0
    this.totalProducts = 0
    this.autoAddProductsTotalPrice = 0
    this.minimumOrderValueTotalPrice = 0
    this.activeDiscounts = []

    // Add the prices of the products.
    this.totalPrice = this.calculateProductTotalPrice(this.basketProducts, this.deliveryType)

    // Count the total products
    this.totalProducts = this.calculateTotalProducts(this.basketProducts, this.deliveryType)

    // Auto added products
    this.autoAddProducts = []
    if (this.totalProducts > 0) {
      this.autoAddProducts = this.calculateAutoAddProducts(this.totalPrice)
      this.autoAddProductsTotalPrice = this.calculateAutoAddProductPrice(this.autoAddProducts, this.deliveryType)
      this.totalPrice = this.totalPrice + this.autoAddProductsTotalPrice
    }

    // Discounts
    if (this.totalProducts > 0) {
      if (this.restaurant.discounts !== undefined && this.restaurant.discounts.length > 0) {
        this.activeDiscounts = this.calculateDiscounts()
      }
    }

    for (var i = 0; i < this.activeDiscounts.length; i++) {
      var activeDiscount = this.activeDiscounts[i]
      this.totalPrice = Utils.toMoney(parseFloat((this.totalPrice - activeDiscount.totalPrice)), 2)
    }

    // MOV (product MOV - discount MOV)
    this.minimumOrderValueTotalPrice = this.calculateMOVPriceFromDiscount(this.basketProducts, this.activeDiscounts, this.deliveryType)

    // It is possible that it will below 0, because of the discount.
    if (this.minimumOrderValueTotalPrice < 0) {
      this.minimumOrderValueTotalPrice = 0
    }
    // console.log("!!", this.minimumOrderValueTotalPrice);

    // Total price, delivery and transaction costs excluded.
    this.baseTotalPrice = this.totalPrice

    // Only applied to delivery.
    this.deliveryCosts = 0
    this.isCheckoutAllowed = true
    if (this.deliveryType === ENUM.DeliveryMethod.Type.Delivery) {
      // Minimum order requirement and basket check
      this.isCheckoutAllowed = false
      if (this.validateMinimumAmount(this.minimumOrderValueTotalPrice) && this.minimumOrderValueTotalPrice > 0) {
        this.isCheckoutAllowed = true
      }

      // Delivery costs
      this.deliveryCosts = this.calculateDeliveryCosts(this.totalPrice)
    }

    this.totalPrice = Utils.toMoney(parseFloat((this.totalPrice + this.deliveryCosts)), 2)

    // Voucher
    if (this.voucher !== null) {
      this.voucherValidation.minimumAmount = this.voucher.validateMinimumAmount(this.totalPrice)
      this.voucherValidation.productRequired = this.voucher.validateVoucherProduct(this.basketProducts)
      if (this.voucherValidation.minimumAmount && this.voucherValidation.productRequired) {
        this.totalPrice = Utils.toMoney(parseFloat((this.totalPrice - this.voucher.calculateVoucherDiscount(this.totalPrice))), 2)
      }
    }

    // Transaction costs
    if (this.paymentMethod != null) {
      this.transactionCosts = this.paymentMethod.calculateTransactionCosts(this.totalPrice)
      this.totalPrice += this.transactionCosts
      if (this.voucher !== null) {
        this.voucher.remainingPayment += this.transactionCosts
      }
    }

    this.totalPrice = Utils.toMoney(this.totalPrice, 2)

    // console.log("Base total price: " + this.baseTotalPrice);
    // console.log("Delivery costs: " + this.deliveryCosts);
    // console.log("Transaction costs: " + this.transactionCosts);
    // console.log("Total price: " + this.totalPrice);
    // console.log("isCheckoutAllowed: " + this.isCheckoutAllowed);
  }

  /**
   * @param {Array} foundCombinations - product id's from the discount combinations
   * Lowers the comboCounter for each basket product in the found discount combination
   */
  lowerComboCounter (foundCombinations) {
    if (foundCombinations && Array.isArray(foundCombinations)) {
      foundCombinations.forEach(productId => {
        const foundProduct = this.basketProducts.find(product => (product.getId() === productId) && (product.comboCounter > 0))
        if (foundProduct) {
          return foundProduct.lowerComboCounter()
        }
        throw new Error('Cannot lower combocounter')
      })
    }
  }

  /**
   *
   * @param {Product[]} basketProductsIds
   * @param {Array[]} discountCombinations
   * @returns {Array} - Array of keys from the found combination
   */
  findCombination (basketProductsIds, discountCombinations) {
    const combination = []
    const copyBasketProducts = basketProductsIds.slice()
    // create an array with only the product id,s
    // [{id:1, amount: 3}, {id:2, amount: 1}] ==> [1,1,1,2]
    const validCombination = discountCombinations.every((row) => {
      return row.some(item => {
        const indexFound = copyBasketProducts.indexOf(item)
        if (indexFound !== -1) {
          const product = copyBasketProducts.splice(indexFound, 1)
          combination.push(product[0])
          return true
        }
      })
    })
    if (validCombination) {
      return combination
    }
  }

  /**
   * Calculate possible discounts.
   * @param {float} totalPrice the price which the discounts will be calculated from.
   * TODO SPLIT calculation with strategy pattern, use ES6
   */
  calculateDiscounts () {
    var discounts = []

    if (this.totalProducts.length == 0) {
      return discounts
    }

    for (var x in this.restaurant.discounts) {
      var discount = this.restaurant.discounts[x]

      // Checks if the discount is available for today.
      if (!discount.isAvailable(this.serverDateTime)) {
        continue
      }

      // Check if the discount supports the selected delivery method.
      if (discount.deliveryType !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        if (discount.deliveryType !== this.deliveryType) {
          continue
        }
      }

      switch (Number(discount.type)) {
        case Discount.Type.Product:
          // console.log("Discount Product");
          for (let xx in this.basketProducts) {
            var basketProduct = this.basketProducts[xx]

            if (basketProduct.isDisabled) {
              continue
            }

            var isMaxAmountReached = false

            for (let xxx in discount.productIds2D[0]) {
              var productId = discount.productIds2D[0][xxx]
              let basketProductId = basketProduct.id
              if (basketProduct.size && basketProduct.size.id) {
                basketProductId = basketProduct.size.id
              }
              if (basketProductId === productId) {
                for (let i = 0; i < basketProduct.count; i++) {
                  // Check whether the discount got a maximum or not. 0 means unlimited.
                  if (discount.number > 0) {
                    // Get the current total of this discount that is in the basket.
                    var amount = 0
                    if (this.activeDiscounts.length > 0) {
                      amount = this.activeDiscounts.reduce(function (n, activeDiscount) {
                        return n + (activeDiscount.id === discount.id)
                      })
                    }
                    // console.log("amount of discounts: " + amount);
                    if (amount >= discount.number) {
                      // Maximum got reached, up to the next product.
                      isMaxAmountReached = true
                      break
                    }
                  }
                  // Whether side dish is included in the price or not.
                  var price = discount.inclusiveSidedishes ? basketProduct.getPrice(this.deliveryType) : basketProduct.getBasePrice(this.deliveryType)
                  var totalDiscount = this.calculateDiscountPrice(discount, price)
                  var movPrice = this.calculateProductAndDiscountMOVPrice(basketProduct, discount, this.deliveryType)
                  this.addDiscount(discounts, discount, totalDiscount, movPrice, [basketProduct.productName])
                }
                if (isMaxAmountReached) {
                  break
                }
              }
              if (isMaxAmountReached) {
                break
              }
            }
          }
          break
        case Discount.Type.Nth:
          // console.log("Discount Nth");
          var nthValidProducts = []
          var discountNumber = Number(discount.number)

          for (let xx in discount.productIds2D[0]) {
            var productId = discount.productIds2D[0][xx]
            for (let xxx in this.basketProducts) {
              var basketProduct = this.basketProducts[xxx]

              if (basketProduct.isDisabled) {
                continue
              }
              let basketProductId = basketProduct.id
              if (basketProduct.size && basketProduct.size.id) {
                basketProductId = basketProduct.size.id
              }
              if (basketProductId === productId) {
                for (let i = 0; i < basketProduct.count; i++) {
                  // delivery / pickup price
                  nthValidProducts.push({
                    price: discount.inclusiveSidedishes ? basketProduct.getPrice(this.deliveryType) : basketProduct.getBasePrice(this.deliveryType),
                    product: basketProduct
                  })
                }
              }
            }
          }

          // Doesn't meet the required number, break.
          if (nthValidProducts.length < discountNumber) {
            break
          }

          // Sort prices ascending
          // nthValidProducts.sort((a, b) => (a.price - b.price))
          nthValidProducts.sort(function (a, b) {
            // console.log(a);
            return a.price - b.price
          })

          // calculate the maximum possible discounts (rounding to floor is also possible of course, but this is faster).
          var maxPossibleDiscounts = (nthValidProducts.length - (nthValidProducts.length % discountNumber)) / discountNumber
          // console.log("maxPossibleDiscounts: " + maxPossibleDiscounts);
          for (let i = 0; i < maxPossibleDiscounts; i++) {
            // The discount will be on the cheapest which starts at the beginning of the array.
            var nthValidProduct = nthValidProducts[i]
            var totalDiscount = this.calculateDiscountPrice(discount, nthValidProduct.price)

            // XFM sidedish
            var movPrice = this.calculateProductAndDiscountMOVPrice(nthValidProduct.product, discount, this.deliveryType)
            this.addDiscount(discounts, discount, totalDiscount, movPrice, [nthValidProduct.product.productName])

            // If it's not a discount repeat, break.
            if (!discount.isDiscountRepeat) {
              break
            }
          }
          break
        case Discount.Type.Combination:
          if (this.basketProducts && Array.isArray(this.basketProducts)) {
            this.basketProducts.forEach(product => product.resetComboCounter())
          }

          let discountCombinationFound
          do {
            let basketProductIds = []
            this.basketProducts.forEach((product) => {
              basketProductIds = basketProductIds.concat(Array(product.comboCounter).fill(product.getId()))
            })

            discountCombinationFound = this.findCombination(basketProductIds, discount.productIds2D)

            let priceCombo = 0
            let minimumOrderValuePrice = 0
            if (discountCombinationFound) {
              const allComboNames = []

              this.lowerComboCounter(discountCombinationFound)
              discountCombinationFound.forEach(productId => {
                const basketProduct = this.basketProducts.find(product => product.getId() === productId)
                if (basketProduct) {
                  // Whether side dish is included in the price or not.
                  const addPrice = discount.inclusiveSidedishes
                    ? basketProduct.getPrice(this.deliveryType)
                    : basketProduct.getBasePrice(this.deliveryType)
                  priceCombo = priceCombo + addPrice
                  minimumOrderValuePrice = minimumOrderValuePrice + basketProduct.getMinimumOrderValueTotalPrice(discount.inclusiveSidedishes, this.deliveryType)
                  allComboNames.push(basketProduct.productName)
                }
              })
              movPrice = this.calculateDiscountPrice(discount, minimumOrderValuePrice)
              totalDiscount = this.calculateDiscountPrice(discount, priceCombo)
              this.addDiscount(discounts, discount, totalDiscount, movPrice, allComboNames)
            }
          } while (discountCombinationFound)
          break

        case Discount.Type.Order:
          // console.log("Discount Order");
          var totalPrice = this.autoAddProductsTotalPrice
          // if (discount.inclusiveSidedishes) {
          totalPrice = this.totalPrice
          // } else {
          //   // Turned off, because PHP and Apps doesn't got this solved
          //   // [UNIT TEST] Discount Suite "Order discount from 10.00 exclusive sidedishes"
          //   // https://yourdelivery.atlassian.net/browse/FED-2796
          //   for (var x in this.basketProducts) {
          //     var basketProduct = this.basketProducts[x]
          //     if (basketProduct.isDisabled)
          //       continue
          //     totalPrice += basketProduct.getBaseTotalPrice(this.deliveryType)
          //   }
          // }

          // MOV
          var movProductPrice = this.getMOVPrice(this.basketProducts, this.deliveryType)
          var movPrice = this.calculateDiscountPrice(discount, movProductPrice)
          if (discount.from <= totalPrice) {
            var totalDiscount = this.calculateDiscountPrice(discount, totalPrice)
            this.addDiscount(discounts, discount, totalDiscount, movPrice, [])
          }
          break
        default:
          // console.log('Wrong discount type: ' + discount.type)
          break
      }
    }
    return discounts
  }

  /**
   * Calculate the discount price.
   * @param {Discount} discount model is from Restaurant.discount.
   * @param {float} price the price the discount will be calcualted on.
   * @return {float} the amount of discount.
   */
  calculateDiscountPrice (discount, price) {
    let result = 0
    // console.log("apply discount on price: " + price);
    if (discount.specialPrice) {
      // console.log(discount.discountSpecialPrice);
      result = Math.max(price - Number(discount.specialPrice), 0)
    } else if (discount.amount) {
      // console.log(discount.discountAmount);
      result = Math.max(Number(discount.amount), 0)
    } else if (discount.percentage) {
      // console.log(discount.percentage + "%");
      result = Utils.toMoney(parseFloat((price * Number(discount.percentage, 0) / 100)), 2)
    }
    // console.log(result);
    return result
  }

  /**
   * Creates a discount object.
   * @param {array} activeDiscounts a array of discounts
   * @param {Discount} discount object.
   * @param {float} price discount price
   * @param {float} xfmSideDishPrice exclusive discount sidedish price that doesn't count MOV
   * @param {Array<String>} productNames the names of the products
   */
  addDiscount (activeDiscounts, discount, price, movDiscount, productNames) {
    var filtered = activeDiscounts.filter(function (activeDiscount) {
      return activeDiscount.id === discount.id && activeDiscount.price === price && activeDiscount.movDiscount === movDiscount
    })
    if (filtered.length > 0) {
      var activeDiscount = filtered[0]
      activeDiscount.count++
      activeDiscount.updateTotalPrice()
    } else {
      var activeDiscount = new ActiveDiscount()
      activeDiscount.id = discount.id
      activeDiscount.price = price
      activeDiscount.movDiscount = movDiscount
      activeDiscount.discountName = discount.discountName
      activeDiscount.description = discount.description
      activeDiscount.count = 1
      activeDiscount.productNames = productNames
      activeDiscount.updateTotalPrice()
      activeDiscounts.push(activeDiscount)
    }
  }

  /**
   * Calculate delivery costs
   * @param {float} totalPrice the amount of costs which the delivery costs needs to be calculated from.
   * WARNING: deliveryCosts.from and .till are generated by PHP which is a string. You need to parseFloat.
   */
  calculateDeliveryCosts (totalPrice) {
    if (this.deliveryArea === undefined) {
      return 0
    }

    let costObject
    let deliveryCosts = 0

    if (totalPrice > 0) {
      for (let x in this.deliveryArea.deliveryCosts) {
        const cost = this.deliveryArea.deliveryCosts[x]
        const from = parseFloat(cost.from)
        const till = parseFloat(cost.till)

        if (totalPrice >= from && (totalPrice < till || till === 0)) {
          costObject = cost
          break
        }
      }
    }

    if (costObject) {
      deliveryCosts = parseFloat(costObject.costs)
    }

    if (typeof currentRestaurant === 'object' &&
      currentRestaurant.hasOwnProperty('dynamicDeliveryCosts') &&
      currentRestaurant.dynamicDeliveryCosts > 0
    ) {
      deliveryCosts += parseFloat(currentRestaurant.dynamicDeliveryCosts)
    }

    return deliveryCosts
  }

  calculateProductTotalPrice (basketProducts, deliveryType) {
    var result = 0
    for (var i = 0; i < basketProducts.length; i++) {
      var basketProduct = basketProducts[i]
      if (basketProduct.isDisabled) {
        continue
      }
      result += Utils.toMoney(basketProduct.getTotalPrice(deliveryType), 2)
    }
    return Utils.toMoney(result, 2)
  }

  calculateTotalProducts (basketProducts, deliveryType) {
    var result = 0
    for (var i = 0; i < basketProducts.length; i++) {
      var basketProduct = basketProducts[i]
      if (basketProduct.isDisabled) {
        continue
      }
      if (basketProduct.deliveryType !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        if (deliveryType !== basketProduct.deliveryType) {
          continue
        }
      }
      result += basketProduct.count
    }
    return result
  }

  /**
   * Calculate auto add products.
   * @param {decimal} totalPrice the price which the auto add products needs to be calculated from.
   * @return {Array<Products>}
   */
  calculateAutoAddProducts (totalPrice) {
    var products = []
    if (this.basketProducts.length === 0) {
      return products
    }

    // WARNING, autoAddProducts is an object literal generated in PHP.
    for (var x in this.restaurant.autoAddProducts) {
      var item = this.restaurant.autoAddProducts[x]
      var type = parseInt(item.deliverytype)
      if (type !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        if (type !== this.deliveryType) {
          return products
        }
      }
      var totalCount = 1
      if ((item.eachamount !== false) && (item.eachamount !== 0) && (totalPrice > 0)) {
        totalCount = Math.ceil(totalPrice / item.eachamount)
      }

      var totalPriceItem = 0
      totalPriceItem = parseFloat(item.price * totalCount)
      // console.log("!: " + item.name);
      var autoAddProduct = new AutoAddProduct()
      autoAddProduct.productName = item.name
      autoAddProduct.id = item.id
      autoAddProduct.count = totalCount
      autoAddProduct.price = item.price
      autoAddProduct.pricePickup = item.price_pickup
      autoAddProduct.deliveryType = parseInt(item.deliverytype)
      autoAddProduct.updateTotalPrice()
      products.push(autoAddProduct)
    }
    return products
  }

  /**
   * Get the total auto add product price.
   * @param {Array<AutoAddProduct>} autoAddProducts
   * @param {DeliveryMethod} deliveryType
   * @return The total price of auto add product.
   */
  calculateAutoAddProductPrice (autoAddProducts, deliveryType) {
    var result = 0
    for (var i = 0; i < autoAddProducts.length; i++) {
      var autoAddProduct = autoAddProducts[i]
      if (autoAddProduct.deliveryType === ENUM.DeliveryMethod.Type.DeliveryAndPickup || autoAddProduct.deliveryType === deliveryType) {
        result += autoAddProduct.getTotalPrice(deliveryType)
      }
    }
    return Utils.toMoney(result, 2)
  }

  /**
   * Get the MOV from basket product and discount.
   * @param {Product} basketProduct
   * @param {Discount} discount
   * @param {DeliveryMethod} deliveryType
   * @return MOV price from the given product and discount.
   */
  calculateProductAndDiscountMOVPrice (basketProduct, discount, deliveryType) {
    var movProductPrice = basketProduct.getMinimumOrderValueTotalPrice(discount.inclusiveSidedishes, deliveryType)
    if (movProductPrice == 0) {
      return movProductPrice
    }

    return this.calculateDiscountPrice(discount, movProductPrice)
  }

  /**
   * Get the total MOV price from basket products.
   * @param {Array<Product>} basketProducts
   * @param {DeliveryMethod} deliveryType
   */
  getMOVPrice (basketProducts, deliveryType) {
    var movPrice = 0

    // Calculate the total XFM prices
    for (var i = 0; i < basketProducts.length; i++) {
      var basketProduct = basketProducts[i]
      if (basketProduct.isDisabled) {
        continue
      }
      if (basketProduct.deliveryType !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        if (deliveryType !== basketProduct.deliveryType) {
          continue
        }
      }
      if (basketProduct.ignoreMinimumOrderValue) {
        continue
      }

      movPrice += basketProduct.getMinimumOrderValueTotalPrice(true, deliveryType) * basketProduct.count
    }
    return movPrice
  }

  /**
   * Get the MOV price by Total MOV - discounts.
   * @param {Array<Product>} basketProducts
   * @param {Array<Discount>} activeDiscounts
   * @param {DeliveryMethod} deliveryType
   */
  calculateMOVPriceFromDiscount (basketProducts, activeDiscounts, deliveryType) {
    // Calculate the total XFM prices
    var movPrice = this.getMOVPrice(basketProducts, deliveryType)

    // Active discount got its own rule what happened with XFM
    for (var i = 0; i < activeDiscounts.length; i++) {
      var discount = activeDiscounts[i]
      movPrice -= discount.movDiscount * discount.count
    }
    // console.log(movPrice);
    return Utils.toMoney(movPrice, 2)
  }

  /**
   * Add product to the basket. If the product is already added, then the product count will be increased.
   * @param {Product} product object passed by the controller.
   * @param {Integer|null} minAge
   * @return {Basket.PRODUCT} the result when trying to add a product.
   */
  addProduct (product, minAge = null) {
    if (
      this.restaurant.onlineStatus === Restaurant.OpeningStatus.Closed ||
      this.restaurant.openingStatus === Restaurant.OpeningStatus.Closed
    ) {
      return Basket.PRODUCT.RESTAURANT_CLOSED
    }

    // It shouldn't be null, but the current implentation doesn't have categories.
    if (product.categoryId !== null) {
      var category = this.restaurant.getCategoryById(product.categoryId)
      if (category !== undefined) {
        if (category.status === Category.Status.Closed) {
          return Basket.PRODUCT.CATEGORY_UNAVAILABLE
        }
      }
    }

    var uniqueId = this.generateProductOrderId(product)
    var filtered = this.basketProducts.filter(function (basketProduct) {
      return basketProduct.uniqueId === uniqueId
    })

    let basketProduct

    if (filtered.length > 0) {
      basketProduct = filtered[0]
      basketProduct.count += product.count
      basketProduct.updateTotalPrice()
    } else {
      product.uniqueId = uniqueId
      product.updateTotalPrice()
      // Copy the data from the original product.
      basketProduct = new Product(product)
      this.basketProducts.push(basketProduct)
      if(Number.isInteger(minAge) && minAge > 0) {
        this.ageRestrictionsCart.push({uniqueId: uniqueId, minAge: minAge})
      }
    }

    this.saveBasket()
    this.calculateTotalPrice()

    if(typeof CookieWall !== 'undefined' && typeof CookieWall.closeCookieWallThisSession === 'function'){
      CookieWall.closeCookieWallThisSession();
    }

    return Basket.PRODUCT.SUCCESS
  }

  /**
   * Increase product that is in the basket by 1.
   * @param {string} id The basket product id, which is unique (product id + side dishes ids).
   */
  increaseProduct (id) {
    var filtered = this.basketProducts.filter(function (product) {
      return product.uniqueId === id
    })

    if (filtered.length > 0) {
      var product = filtered[0]
      product.count++
      product.updateTotalPrice()
    }
    this.saveBasket()
    this.calculateTotalPrice()
  }

  /**
   * Decrease product that is in the basket by 1. If the product count is 1, remove them all.
   * @param {string} id The basket product id, which is unique (product id + side dishes ids).
   * @return {boolean} whether the product is removed or not.
   */
  decreaseProduct (id) {
    var filtered = this.basketProducts.filter(function (product) {
      return product.uniqueId === id
    })

    if (filtered.length > 0) {
      var product = filtered[0]
      product.count--
      if (product.count <= 0) {
        this.removeProduct(id)
      }
      else {
        product.updateTotalPrice()
      }
    }
    this.saveBasket()
    this.calculateTotalPrice()
    return product.count === 0
  }

  /**
   * Remove product from the basket.
   * @param {string} id The basket product id, which is unique (product id + size + side dishes id).
   */
  removeProduct (id) {
    for (var i = 0; i < this.basketProducts.length; i++) {
      var product = this.basketProducts[i]
      if (product.uniqueId === id) {
        this.basketProducts.splice(i, 1)
        // remove the product from ageRestrictions too
        for(let i = 0; i < this.ageRestrictionsCart.length; i++) {
          if(id === this.ageRestrictionsCart[i].uniqueId ) {
            this.ageRestrictionsCart.splice(i,1)
            break
          }
        }

        break
      }
    }

    // TODO VOUCHER??? REMOVED?
    if (this.basketProducts.length == 0) {
      this.removeVoucher()
    }
    this.saveBasket()
    this.calculateTotalPrice()
  }

  /**
   * Add comment to a product. HTML tags will be stripped and white spaces will be trimmed, html tags will be removed and it will check on maximum length.
   * @param {string} id The basket product id, which is unique (product id + size + side dishes id).
   * @param {string} comment The comment that will be added to the product.
   */
  addComment (id, comment) {
    var filtered = this.basketProducts.filter(function (product) {
      return product.uniqueId === id
    })

    if (filtered.length > 0) {
      var product = filtered[0]
      comment = comment.replace(/<(?:.|\n)*?>/gm, '')
      comment = comment.replace(/(^\s+|\s+$)/g, '')
      if (comment.length > Product.CommentMaxLength) {
        comment.substring(0, Product.CommentMaxLength)
      }
      product.comment = comment
      this.saveBasket()
    }
  }

  /**
   * Get the comment of a product.
   * @param {string} id The basket product id, which is unique (product id + size + side dishes id).
   * @return {string} the commment of the product.
   */
  getComment (id) {
    var filtered = this.basketProducts.filter(function (product) {
      return product.uniqueId === id
    })
    if (filtered.length > 0) {
      var product = filtered[0]
      return product.comment
    }
    return null
  }

  /**
   * Validate if the price meets the minimum requirement.
   * @param {float} price the price the minimum order amount must be validated on.
   * @return {boolean} Whether the validation is a success or not.
   */
  validateMinimumAmount (price) {
    if (this.deliveryArea === undefined) {
      return false
    }

    if (this.totalProducts == 0) {
      return false
    }

    if (this.deliveryType === ENUM.DeliveryMethod.Type.Pickup) {
      return true
    }

    if (price >= this.deliveryArea.minimumOrderAmount) {
      return true
    }
    return false
  }

  /**
   * Checks if there is an minimumOrderAmount
   * @return {boolean} Is there an minimum order amount true/false
   */
  hasMinimumAmount () {
    return this.deliveryArea.minimumOrderAmount !== 0
  }

  /**
   * Calculate the amount that is needed for minimum requirement by total price.
   * @param {float} price the price the minimum order amount must be calculated on.
   * @return {float} The difference between the total price and minimum order amount.
   * When it is a negative number, the sum will be 0.
   */
  calculateMinimumAmount (price) {
    if (price < this.deliveryArea.minimumOrderAmount) {
      return Utils.toMoney((this.deliveryArea.minimumOrderAmount - price), 2)
    }
    return 0
  }

  /**
   * It validates if the products are available with the given time.
   * It will only return the first invalid category.
   * @param {moment} deliveryTime The time the user want to
   * @return {object} the invalid category and product if there is one.
   */
  validateDeliveryTime (deliveryTime) {
    for (let x in this.basketProducts) {
      let basketProduct = this.basketProducts[x]
      let category = this.restaurant.getCategoryById(basketProduct.categoryId)
      if (category !== undefined) {
        let status = category.validateOrderTime(deliveryTime, deliveryTime.day())
        if (
          status !== Category.Status.Open &&
          (
            basketProduct.deliveryType === ENUM.DeliveryMethod.Type.DeliveryAndPickup ||
            basketProduct.deliveryType === this.deliveryType
          )
        ) {
          return { category: category, product: basketProduct }
        }
      }
    }
    return null
  }

  /**
   * Set the voucher in the basket.
   * @param {Takeaway.Voucher} voucher object
   */
  addVoucher (voucher) {
    this.voucher = voucher
    this.saveBasket()
    this.calculateTotalPrice()
  }

  /**
   * Remove the voucher from the basket.
   */
  removeVoucher () {
    this.voucher = null
    this.saveBasket()
    this.calculateTotalPrice()
  }

  /**
   * Set paymentmethod.
   * @param {PaymentMethod} paymentmethod
   */
  setPaymentMethod (id) {
    for (var i = 0; i < this.restaurant.paymentMethods.length; i++) {
      var paymentMethod = this.restaurant.paymentMethods[i]
      if (paymentMethod.id === id) {
        this.paymentMethod = paymentMethod
        break
      }
    }
    this.saveBasket()
    this.calculateTotalPrice()
  }

  /**
   * Remove paymentmethod.
   */
  removePaymentMethod () {
    this.paymentMethod = null
    this.saveBasket()
    this.calculateTotalPrice()
  }

  /**
   * return the maximum age value the customer has consent for
   * @returns {number}
   */
  getMinAgeConsentValue() {
    let minAges = []
    for(let i=0; i < this.ageRestrictionsCart.length; i++) {
      minAges.push(this.ageRestrictionsCart[i].minAge)
    }
    return Math.max(...minAges)
  }

  /**
   * get the minAge restriction for certain product
   * if exist in the cart.
   * @param uniqueId
   * @returns {Integer|null}
   */
  ageRestrictionValue(uniqueId) {
    for(let i=0; i < this.ageRestrictionsCart.length; i++) {
      if(uniqueId === this.ageRestrictionsCart[i].uniqueId) {
        return this.ageRestrictionsCart[i].minAge
      }
    }
    return null
  }

  /**
   * Save the basket into localstorage
   * // TODO move to BasketRepository
   */
  saveBasket () {
    // Delete helper that got added in Products.
    for (var x in this.basketProducts) {
      var value = this.basketProducts[x]
      delete value.comboCounter
    }

    var content = JSON.parse(localStorage.getItem(ENUM.Persistence.BASKET) || '{}')
    content[this.id] = {
      products: this.basketProducts,
      voucher: this.voucher,
      paymentMethod: this.paymentMethod,
      ageRestrictionsCart: this.ageRestrictionsCart
    }

    localStorage.setItem(ENUM.Persistence.BASKET, JSON.stringify(content))
    localStorage.setItem(ENUM.Persistence.LAST_INTERACTION_TIME, Date.now())
  }

  /**
   * Load basket from the local storage. If the max time has exceeded, then the basket will be cleared.
   * // TODO move to BasketRepository
   */
  loadBasket () {
    if (Date.now() > this.getLastInteractionTime() + this.Time.TTL) {
      this.clearBaskets()
    } else {
      this.clearBasket()
      localStorage.setItem(ENUM.Persistence.LAST_INTERACTION_TIME, Date.now())
      const baskets = JSON.parse(localStorage.getItem(ENUM.Persistence.BASKET) || '{}')

      if (baskets.hasOwnProperty(this.id)) {
        const basket = baskets[this.id]
        const products = basket.products
        const ageRestrictionsCart = basket.ageRestrictionsCart

        for (let i = 0; i < products.length; i++) {
          let basketProduct = new Product(products[i])
          basketProduct.updateTotalPrice()
          this.basketProducts.push(basketProduct)
        }

        if (basket.voucher !== null) {
          this.voucher = new Voucher(basket.voucher)
        }

        if (basket.paymentMethod !== null) {
          this.paymentMethod = new PaymentMethod(basket.paymentMethod)
        }

        if(ageRestrictionsCart !== null && typeof ageRestrictionsCart !== 'undefined') {
          for (let i = 0; i < ageRestrictionsCart.length; i++) {
            this.ageRestrictionsCart.push(ageRestrictionsCart[i])
          }
        }
      }
    }
  }

  /**
   * Clear the basket, products, auto added products, voucher.
   */
  clearBasket () {
    this.basketProducts = []
    this.autoAddProducts = []
    this.voucher = null
    this.paymentMethod = null
    this.ageRestrictionsCart = []
  }

  /**
   * Clear all baskets in the local storage.
   * TODO: move to BasketRepository
   */
  clearBaskets () {
    localStorage.setItem(ENUM.Persistence.BASKET, '{}')
    this.clearBasket()
  }

  /**
   * Get total products.
   * @param {string} id The id of the restaurant.
   * @return the number of products in the basket.
   */
  getTotalProducts () {
    let count = 0
    for (let i = 0; i < this.basketProducts.length; i++) {
      let basketProduct = this.basketProducts[i]
      count += basketProduct.count
    }
    return count
  }

  /**
   * Returns total MOV products.
   * @return {int} total MOV products.
   */
  getTotalProductsMOVOnly () {
    var count = 0
    for (var i = 0; i < this.basketProducts.length; i++) {
      var basketProduct = this.basketProducts[i]
      if (basketProduct.ignoreMinimumOrderValue) {
        continue
      }
      count += basketProduct.count
    }
    return count
  }

  /**
   * Get total products by restaurant id.
   * @param {string} id The id of the restaurant.
   * @return the number of products in the basket.
   */
  getTotalProductsByRestaurantId (id) {
    let count = 0
    if (Date.now() > this.getLastInteractionTime() + this.Time.TTL) {
      this.clearBaskets()
      return count
    } else {
      const baskets = JSON.parse(localStorage.getItem(ENUM.Persistence.BASKET) || '{}')
      if (baskets.hasOwnProperty(id)) {
        let products = baskets[id].products
        for (var i = 0; i < products.length; i++) {
          count += products[i].count
        }
        return count
      }
      return count
    }
  }

  /**
   * Whether the basket contains products that exclude minimum order value.
   * @return {boolean}
   */
  containsExcludeProduct () {
    if (this.deliveryType == ENUM.DeliveryMethod.Type.Pickup) {
      return false
    }

    for (var x in this.basketProducts) {
      var basketProduct = this.basketProducts[x]
      if (basketProduct.ignoreMinimumOrderValue) {
        return true
      }

      if (basketProduct.size !== null) {
        if (basketProduct.size.ignoreMinimumOrderValue) {
          return true
        }
        for (var xx in basketProduct.size.sideDishes) {
          var sideDish = basketProduct.size.sideDishes[xx]
          if (sideDish.ignoreMinimumOrderValue) {
            return true
          }
        }
      }
    }
    return false
  }

  /**
   * Validate restaurant if it is possible to switch delivery method.
   */
  validateRestaurant () {
    var result = false
    if (this.restaurant.deliveryType === ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
      result = true
    }

    return {
      restaurantSupportedDeliveryType: result
    }
  }

  /**
   * Products will be validated if they are available when switched delivery method.
   * @param {Takeaway.DeliveryType} deliveryType
   */
  validateBasket (deliveryType) {
    var unsupportedDeliveryType = []
    var priceDifference = []

    for (var x in this.basketProducts) {
      var basketProduct = this.basketProducts[x]
      var isUnsupportedHelper = false
      // Check wether the product is valid for the delivery type.
      if (basketProduct.deliveryType !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        if (basketProduct.size !== null) {
          if (basketProduct.size.deliveryType !== deliveryType) {
            unsupportedDeliveryType.push(basketProduct)
          }
        } else if (basketProduct.deliveryType !== deliveryType) {
          unsupportedDeliveryType.push(basketProduct)
          isUnsupportedHelper = true
        }
      }

      // We don't need to check the price difference if the product is unsupported.
      if (isUnsupportedHelper) {
        continue
      }

      // We don't need to check the price difference if the product only supports once delivery type,
      if (basketProduct.deliveryType !== ENUM.DeliveryMethod.Type.DeliveryAndPickup) {
        continue
      }

      // Check whether the product got the same base price on diffent delivery methods.
      if (basketProduct.basePrice !== basketProduct.basePricePickup) {
        priceDifference.push(basketProduct)
      }
    }

    return {
      unsupportedDeliveryType: unsupportedDeliveryType,
      priceDifference: priceDifference
    }
  }

  /**
   * Parse reorder data and saves it. Recalculate based on the current delivery method.
   * There is no invalid check, because that is already done in processreorder.js.
   * Warning the data comes from PHP and it is not parsed.
   * @param {json} data
   */
  reorder (data) {
    // Clear the basket first
    this.clearBasket()

    // Put it in the localstorage and load it again
    var content = JSON.parse(localStorage.getItem(ENUM.Persistence.BASKET) || '{}')
    content[this.id] = data[this.id]
    localStorage.setItem(ENUM.Persistence.BASKET, JSON.stringify(content))
    this.loadBasket()
    this.switchDeliveryType(this.deliveryType, true)
    this.saveBasket()
  }

  /**
   * The data comes from the server and it contains the correct values.
   * When a product is mismatched the price will be corrected.
   * When a product is missing the product will be removed from the basket.
   * Warning the data comes from PHP and it is not parsed.
   * @param {json} data
   */
  updateBasketAfterValidation (data) {
    this.productMismatches = []
    this.productMissings = []
    if (data.productMismatch) {
      for (var x in data.productMismatches) {
        var productMismatch = data.productMismatches[x]
        this.productMismatches.push(this.parseProductMismatch(productMismatch))
      }
    }

    if (data.productMissing) {
      var i = data.productMissings.length
      while (i--) {
        var productMissingId = data.productMissings[i]
        var productMissing = this.basketProducts.filter(function (x) {
          return x.uniqueId === productMissingId
        })[0]

        if (productMissing === undefined) {
          continue
        }

        this.productMissings.push(productMissing)
        this.removeProduct(productMissing.uniqueId)
      }
    }
    // Last check if the basket is still complete.
    var count = 0
    for (var i in data.productExistingIds) {
      var basketProduct = this.basketProducts.filter(function (x) {
        if (x.uniqueId === data.productExistingIds[i]) {
          count++
        }
      }.bind(this))
    }
    if (count !== data.productExistingIds.length) {
      // console.log('Something went wrong with the validation, basket got expired probably')
    }

    this.calculateTotalPrice()
    this.saveBasket()
  }

  parseProductMismatch (productMismatch) {
    var basketProduct = this.basketProducts.filter(function (x) {
      return x.uniqueId === productMismatch.uniqueId
    })[0]

    if (basketProduct === undefined) {
      return
    }

    // The data doesn't distinct product and size.
    if (basketProduct.size !== null) {
      basketProduct.size.price = productMismatch.singlePrice
      basketProduct.size.pricePickup = productMismatch.singlePricePickup
      basketProduct.deliveryType = parseInt(productMismatch.deliveryType)
      basketProduct.ignoreMinimumOrderValue = productMismatch.ignoreMinimumOrderValue
    }
    else {
      basketProduct.basePrice = productMismatch.singlePrice
      basketProduct.basePricePickup = productMismatch.singlePricePickup
      basketProduct.deliveryType = parseInt(productMismatch.deliveryType)
      basketProduct.ignoreMinimumOrderValue = productMismatch.ignoreMinimumOrderValue
    }

    for (var x in productMismatch.sideDishes) {
      var sideDishMismatch = productMismatch.sideDishes[x]
      basketProduct.size.sideDishes.filter(function (s) {
        if (s.id === sideDishMismatch.id) {
          s.price = parseFloat(sideDishMismatch.price)
          s.pricePickup = parseFloat(sideDishMismatch.price_pickup)
          s.ignoreMinimumOrderValue = sideDishMismatch.ignoreMinimumOrderValue
          return
        }
      })
    }
    basketProduct.updateTotalPrice()
    return basketProduct
  }

  /**
   * Clear the basket validation data.
   */
  clearBasketValidationData () {
    this.productMismatches = []
    this.productMissings = []
  }

  /**
   * Unused, because the validation will be done with form submit.
   * Products will be send to the server for validation.
   * TODO Should be put in seperate class that handle webservice requests.
   * @param {function} callback will be called when the request is done.
   */
  validateBasketByServer (callback) {
    if (this.restaurant.openingStatus == Restaurant.OpeningStatus.Closed) {
      return
    }

    if (!this.isValidateBasketByServer) {
      this.isValidateBasketByServer = true
    }

    var basketJson = this.getBasketJson()
    var result
    $.ajax({
      type: 'POST',
      url: this.restaurant.cartUrl,
      contentType: 'application/json; charset=utf-8',
      dataType: 'json',
      data: basketJson,
      success: function (response, status, jqXHR) {
        result = response
      },
      error: function (jqXHR, status, error) {
        // TODO nice error message
        // error from the server.
        // console.log('checkout failed', status, error)
      }
    }).always = function () {
      this.isValidateBasketByServer = false
      callback(result)
    }.bind(this)
  }

  /**
   * Get the current basket but then data that is only need for validation.
   * @return {object}
   */
  getBasketJson () {
    var products = []

    for (var x in this.basketProducts) {
      var basketProduct = this.basketProducts[x]
      var product = {
        uniqueId: basketProduct.uniqueId,
        categoryId: basketProduct.categoryId,
        productId: basketProduct.id,
        productName: basketProduct.productName,
        count: basketProduct.count,
        totalPrice: basketProduct.totalPrice,
        comment: basketProduct.comment
      }

      if (basketProduct.size !== null) {
        product.sizeId = basketProduct.size.id
        product.sizeName = basketProduct.size.sizeName

        product.sideDishes = []
        for (var xx in basketProduct.size.sideDishes) {
          var sideDish = basketProduct.size.sideDishes[xx]
          product.sideDishes.push({
            sideDishId: sideDish.id,
            sideDishName: sideDish.sideDishName
          })
        }
      }
      products.push(product)
    }

    let paymentMethodId = null

    if (this.paymentMethod !== null) {
      paymentMethodId = this.paymentMethod.id
    }

    if (this.voucher !== null) {
      if (this.totalPrice === 0) {
        paymentMethodId = PaymentMethod.ID.Voucher
      }
    }

    var basketObject = {
      deliveryAreaId: this.restaurant.deliveryAreaId,
      dynamicDeliveryCosts: this.restaurant.dynamicDeliveryCosts,
      restaurantId: this.id,
      deliveryType: this.deliveryType,
      products: products,
      totalPrice: this.totalPrice,
      paymentMethod: paymentMethodId
    }
    return JSON.stringify(basketObject)
  }

  /**
   * Get the last interaction time of the basket.
   * If it doesn't have a last interaction time, then a new Date will be returned.
   * @return {int} time in seconds.
   */
  getLastInteractionTime () {
    const time = localStorage.getItem(ENUM.Persistence.LAST_INTERACTION_TIME)
    if (time) {
      return parseInt(time)
    } else {
      return (new Date()).getTime()
    }
  }

  /**
   * Generate unique id based on product and side dish ids.
   * @param {Product} product object.
   */
  generateProductOrderId (product) {
    if (product.size === null) {
      return product.id
    }

    var uniqueId = product.size.id
    var sideDishes = product.size.sideDishes
    if (sideDishes === undefined) {
      return uniqueId
    }

    for (var i = 0; i < sideDishes.length; i++) {
      uniqueId += '-' + sideDishes[i].id
    }
    return uniqueId
  }
}
